import {
  Box,
  Button,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import {
  CompanyIdIssuanceInfo,
  CompanyIdIssuanceInputForm,
  CompanyIdIssuanceSearchForm,
} from "IF/interfaceType";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import axios from "axios";
import { MessageModal } from "./MessageModal";
import { useSessionStorage } from "IF/userSession";
import CompanyIdIssuanceTable from "./CompanyIdIssuanceTable";
import CompanyIdIssuanceRegister from "./CompanyIdIssuanceRegister";
import { FaPlus } from "react-icons/fa";
import CompanyIdIssuanceSearch from "./CompanyIdIssuanceSearch";
import { DataContext } from "Contexts/DataProvider";
import ConfirmationModal from "./ConfirmationModal";
import { createTitle } from "Utils/format";
import CompanyIdIssuanceCard from "./CompanyIdIssuanceCard";
export const CompanyIdIssuance = () => {
  // ユーザ情報
  const { getUserData, setUserData } = useSessionStorage();
  const userId: string = getUserData()?.id;
  const userData = getUserData();
  const { companyCode: userCompanyCode } = userData;

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { setChoiceMenu, toggleMenu } = sidebarContext;

  // サイドバー企業プルダウン用
  const companyListContext = useContext(DataContext);
  if (!companyListContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { setCompanyListData, setSelectedCompanyCode } = companyListContext;

  const theme = useTheme();

  // 新規登録モーダル開閉
  const [open, setOpen] = useState(false);
  const initData: CompanyIdIssuanceInfo = {
    companyCode: "",
    companyName: "",
    status: "",
    mailSendLimit: 5,
    smsSendLimit: 5,
    createUser: userId,
    updateUser: userId,
  };
  const initForm: CompanyIdIssuanceSearchForm = {
    companyCode: "",
    companyName: "",
    status: "",
  };
  // 確認モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);
  const handleConfirmModalOpen = (
    message: string,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  // 登録済み企業コード一覧
  const [data, setData] = useState<CompanyIdIssuanceInfo[]>([]);
  // 検索フォーム
  const [searchForm, setSearchForm] =
    useState<CompanyIdIssuanceSearchForm>(initForm);
  // 登録フォーム
  const [form, setForm] = useState<CompanyIdIssuanceInputForm>(initData);
  // 編集フォーム
  const [editFormData, setEditFormData] =
    useState<CompanyIdIssuanceInfo>(initData);
  // 編集対象の行のindex
  const [editIndex, setEditIndex] = useState<number | null>(null);

  // 初期登録済み企業コード一覧
  const [initEditFormData, setInitEditFormData] = useState<
    CompanyIdIssuanceInfo[]
  >([]);

  // レイアウト設定
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  // モーダル
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };

  // 新規登録開閉
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setForm(initData);
  };

  // タイトル作成
  const title = "契約企業管理";
  useEffect(() => {
    document.title = createTitle(title);
    setChoiceMenu(title);
    toggleMenu("YKC専用");
  }, [setChoiceMenu, toggleMenu]);

  // 初期表示時処理
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight - 360;
      setTableBodyHeight(windowHeight);
    };

    // 初期表示時とリサイズ時に高さを計算
    handleResize();
    window.addEventListener("resize", handleResize);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 初期表示時データ取得処理
  useEffect(() => {
    const fetchData = async () => {
      try {
        // API呼び出し
        const response = await axios.get(
          `${API_BASE_URL}/CompanyIdIssuance/GetAllCompanyInfo`,
          {
            params: {
              companyCode: "",
              companyName: "",
              status: "",
            },
          }
        );
        if (Array.isArray(response.data) && response.data.length !== 0) {
          setData(response.data);
          setInitEditFormData(response.data);
        } else {
          setModalOpen(true);
          setMessage("契約企業がありません。");
        }
      } catch (error) {
        setModalOpen(true);
        setMessage("契約企業の取得に失敗しました");
        console.error("契約企業の取得に失敗しました:", error);
      }
    };
    fetchData();
  }, []);

  // 検索条件入力のハンドラー
  const searchHandleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    let { name, value } = e.target;
    // companyCode の場合は大文字変換
    if (name === "companyCode") {
      value = value.toUpperCase();
    }
    setSearchForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 検索ボタンのハンドラー
  const handleSearch = () => {
    getData(searchForm);
  };

  // フィルターリセットのハンドラー
  const handleReset = () => {
    setSearchForm({
      companyCode: "",
      companyName: "",
      status: "",
    });
  };

  // 新規登録入力のハンドラー
  const inputHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    // companyCode の場合は大文字変換
    if (name === "companyCode") {
      value = value.toUpperCase();
    }

    // mailSendLimit または smsSendLimit で、value が空の場合は "0"
    if (
      (name === "mailSendLimit" || name === "smsSendLimit") &&
      (!value || value.trim() === "")
    ) {
      value = "0";
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // フォーム編集時処理
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = event.target;
    if (
      (name === "mailSendLimit" || name === "smsSendLimit") &&
      (!value || value.trim() === "")
    ) {
      value = "0";
    }
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 編集ボタン押下時の処理
  const handleEdit = (index: number) => {
    setEditIndex(index);
    setEditFormData(data[index]);
  };

  // 編集中のデータ変更
  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = event.target as
      | HTMLInputElement
      | HTMLSelectElement;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 編集キャンセル
  const handleCancel = () => {
    setEditIndex(null);
    setEditFormData(initData);
  };

  //　☆☆☆☆☆☆☆☆☆☆☆☆☆☆☆☆API呼び出し☆☆☆☆☆☆☆☆☆☆☆☆☆☆☆☆

  // 初期表示時のデータ取得処理
  const getData = async (searchForm: CompanyIdIssuanceSearchForm) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/CompanyIdIssuance/GetAllCompanyInfo`,
        {
          params: searchForm,
        }
      );
      setData(response.data);
      setInitEditFormData(response.data);
      handleCancel();
    } catch (error) {
      setModalOpen(true);
      setMessage("契約企業の取得に失敗しました");
      console.error("契約企業の取得に失敗しました:", error);
    }
  };

  // 新規登録処理
  const handleInput = async () => {
    try {
      // API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/CompanyIdIssuance/PostCompanyInfo`,
        form
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("契約企業情報が登録されました。");
        // 再検索用
        handleClose();
        getData(searchForm);
        getCompanyCode();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("契約企業情報の登録に失敗しました。");
        console.error("契約企業情報の登録に失敗しました。", error);
      }
    }
  };

  // 初期データと変更後formを比較
  const getModifiedElements = () => {
    if (
      editIndex != null &&
      (editFormData.status !== initEditFormData[editIndex]?.status ||
        editFormData.mailSendLimit !==
          initEditFormData[editIndex]?.mailSendLimit ||
        editFormData.smsSendLimit !== initEditFormData[editIndex]?.smsSendLimit)
    ) {
      return editFormData;
    } else {
      return {};
    }
  };

  // 更新時処理
  const handleUpdate = async () => {
    // 変更チェック
    let modifiedElements = getModifiedElements();

    // 変更なしの場合更新しない
    if (Object.keys(modifiedElements).length === 0) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    // ユーザIDを設定
    modifiedElements = {
      ...modifiedElements,
      createUser: userId,
      updateUser: userId,
    };
    try {
      const response = await axios.put(
        `${API_BASE_URL}/CompanyIdIssuance/PutCompanyInfo`,
        modifiedElements
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("契約企業情報が更新されました。");
        setEditFormData(initData);
        setEditIndex(null);
        getData(searchForm);
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("契約企業情報の更新に失敗しました");
        console.error("契約企業情報の更新に失敗しました:", error);
      }
    }
  };

  // 削除処理
  const handleDelete = async (index: number) => {
    try {
      // 1. API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/CompanyIdIssuance/DeleteCompanyInfo`,
        data[index]
      );

      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("契約企業情報を削除しました。");
        // データ取得
        getData(searchForm);
        // 企業選択で削除した企業を選択していた場合リセット
        if (userCompanyCode === data[index].companyCode) {
          const user = {
            ...userData,
            companyCode: "",
            companyName: "",
          };
          setUserData(user);
          setSelectedCompanyCode("");
        }
        getCompanyCode();
      }
      // const contentDisposition = response.headers["content-disposition"];
      // // ファイル名取得
      // const fileName = extractFileName(contentDisposition);

      // // 2. Blobを生成
      // const blob = new Blob([response.data], { type: "application/zip" });

      // // 3. Blob URLを作成
      // const url = window.URL.createObjectURL(blob);

      // // 4. ダウンロード用のリンクを作り、クリックしてダウンロードを開始
      // const link = document.createElement("a");
      // link.href = url;
      // link.download = fileName;
      // link.click();

      // // 5. 使用済みBlob URLを解放
      // window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("契約企業情報の削除に失敗しました。");
        console.error("契約企業情報の削除に失敗しました:", error);
      }
    }
  };

  // サイドバー企業選択データ取得
  const getCompanyCode = async () => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/Sidebar/GetAllCompanyCode`
      );
      setCompanyListData(response.data);
    } catch (error) {
      console.error("企業コードの取得に失敗しました:", error);
    }
  };

  return (
    <>
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            padding: 2,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.default",
            color: "black",
            borderRadius: 2,
            boxShadow: 3,
            gap: 1,
            minHeight: tableBodyHeight,
          }}
        >
          <CompanyIdIssuanceRegister
            open={open}
            handleClose={handleClose}
            form={form}
            handleInput={handleInput}
            inputHandleChange={inputHandleChange}
          />
          <CompanyIdIssuanceSearch
            searchForm={searchForm}
            handleSearch={handleSearch}
            handleChange={searchHandleChange}
            handleReset={handleReset}
          />
          {isMd ? (
            <CompanyIdIssuanceCard
              data={data}
              editIndex={editIndex}
              editFormData={editFormData}
              onEdit={handleEdit}
              handleChange={handleChange}
              handleUpdate={handleUpdate}
              handleCancel={handleCancel}
              handleDelete={handleDelete}
              handleTextChange={handleTextChange}
              handleConfirmModalOpen={handleConfirmModalOpen}
              handleOpen={handleOpen}
            />
          ) : (
            <CompanyIdIssuanceTable
              data={data}
              editIndex={editIndex}
              editFormData={editFormData}
              onEdit={handleEdit}
              handleChange={handleChange}
              handleUpdate={handleUpdate}
              handleCancel={handleCancel}
              handleDelete={handleDelete}
              handleTextChange={handleTextChange}
              handleConfirmModalOpen={handleConfirmModalOpen}
              tableBodyHeight={tableBodyHeight}
            />
          )}
          {!isMd && (
            <Button
              variant="contained"
              onClick={handleOpen}
              sx={{ mt: 1, backgroundColor: "#CC5500" }}
              startIcon={<FaPlus />}
            >
              新規登録
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};
export default CompanyIdIssuance;
